import { AttachReference } from "./AttachReference";
import "../../views/workspace/ws-skin.css";
import { shareInMessaging } from '../../api/messaging.api';
import { Check, FilePlus2, LayoutDashboard, Link2, ListTodo, MessageSquare, MoreHorizontal, NotebookPen, Send, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Button, Menu, cx, type MenuEntry } from "../charte";
import {
  referencesApi,
  type WorkspaceReference,
  type ResolvedReference,
} from "../../api/references.api";
import { useAuthStore } from "../../store/auth.store";
import { referenceLabels, referenceStyle, referenceToneClasses, referenceUrl } from "./reference-catalog";
/**
 * Bloc « lien » d'un objet Permio (refonte du 15/10/2026) : « Copier le lien du paiement », partager dans une
 * conversation, et « ··· » (tâche, note, tableau). Libellés et couleur selon l'objet (`referenceStyle`), les mêmes
 * partout. Mêmes actions qu'avant ; le lien conserve les droits de la source et ne copie pas son contenu.
 */
export function CopyReference({
  reference,
  iconOnly = false,
}: {
  reference: WorkspaceReference;
  iconOnly?: boolean;
}) {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const write = ["owner", "manager", "agent"].includes(role ?? "");
  const style = referenceStyle[reference.entityType];
  const tone = referenceToneClasses[style.tone];
  const [status, setStatus] = useState<"ok" | "error" | null>(null);
  const [attach, setAttach] = useState<"note" | "board" | null>(null);
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(null), 2200);
    return () => clearTimeout(t);
  }, [status]);
  const source = encodeURIComponent(referenceUrl(reference));
  const copyLabel = `Copier le lien ${style.of}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(new URL(referenceUrl(reference), window.location.origin).href);
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  };
  const entries: MenuEntry[] = [
    { kind: "label", label: `Relier ${style.the} à…` },
    { label: "Une nouvelle tâche", icon: ListTodo, disabled: !write, onSelect: () => navigate(`/workspace/tasks?create=1&entityType=${reference.entityType}&entityId=${reference.entityId}`) },
    { label: "Une note existante", icon: NotebookPen, disabled: !write, onSelect: () => setAttach("note") },
    { label: "Une nouvelle note", icon: FilePlus2, disabled: !write, onSelect: () => navigate(`/workspace/notes?create=1&source=${source}`) },
    { label: "Un tableau existant", icon: LayoutDashboard, disabled: !write, onSelect: () => setAttach("board") },
    { label: "Un nouveau tableau", icon: LayoutDashboard, disabled: !write, onSelect: () => navigate(`/workspace/boards?create=1&source=${source}`) },
  ];
  if (reference.entityType === "TASK")
    entries.push({ kind: "separator" }, { label: "Commenter cette tâche", icon: MessageSquare, onSelect: () => navigate(`/workspace/tasks?comments=1&taskId=${reference.entityId}`) });
  const Icon = status === "ok" ? Check : style.icon;
  const share = `Partager ${style.the} dans une conversation`;
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div
        className={cx(
          "inline-flex max-w-full items-center gap-0.5 self-start rounded-pill border p-1",
          tone.frame,
        )}
      >
        <button
          type="button"
          onClick={() => void copy()}
          aria-label={status === "ok" ? "Lien copié" : copyLabel}
          title={copyLabel}
          className={cx(
            "inline-flex h-8 min-w-0 cursor-pointer items-center gap-2 rounded-pill border-0 bg-transparent pl-1 text-[13px] font-medium text-heading transition hover:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100",
            iconOnly ? "pr-1" : "pr-3",
          )}
        >
          <span className={cx("grid h-6 w-6 shrink-0 place-items-center rounded-full", status === "ok" ? "bg-emerald-100 text-emerald-600" : tone.chip)} aria-hidden>
            {status === "ok" ? <Icon size={13} /> : <Link2 size={13} />}
          </span>
          {!iconOnly && <span className="truncate">{status === "ok" ? "Lien copié" : status === "error" ? "Copie impossible" : copyLabel}</span>}
        </button>
        <Button size="sm" variant="ghost" iconOnly icon={Send} aria-label={share} title={share} className="!h-8 !w-8 hover:!bg-white" onClick={() => shareInMessaging(reference)} />
        <Menu
          align="end"
          entries={entries}
          trigger={(props) => (
            <Button {...props} size="sm" variant="ghost" iconOnly icon={MoreHorizontal} aria-label={`Relier ${style.the} à une tâche, une note ou un tableau`} title="Tâche, note, tableau" className="!h-8 !w-8 hover:!bg-white" />
          )}
        />
      </div>
      <span role="status" className="sr-only">
        {status === "ok" ? "Lien copié" : status === "error" ? "Copie impossible" : ""}
      </span>
      {attach && (
        <div className="rounded-2xl border border-line bg-surface-light p-3 text-[13px]">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-eyebrow uppercase text-muted">{attach === "note" ? "Ajouter à une note existante" : "Ajouter à un tableau existant"}</span>
            <Button size="sm" variant="ghost" iconOnly icon={X} aria-label="Fermer l’ajout" onClick={() => setAttach(null)} />
          </div>
          <div className="ws-skin">
            <AttachReference reference={reference} kind={attach} onClose={() => setAttach(null)} />
          </div>
        </div>
      )}
    </div>
  );
}
export function ReferenceChip({
  reference,
  item,
  pending,
  error,
}: {
  reference: WorkspaceReference;
  item?: ResolvedReference;
  pending: boolean;
  error: boolean;
}) {
  if (!item?.available)
    return (
      <span>
        {error
          ? "Vérification indisponible"
          : pending
            ? "Vérification…"
            : "Référence indisponible"}
      </span>
    );
  return (
    <details className="business-reference">
      <summary>
        [{referenceLabels[reference.entityType]} · {item.label}]
      </summary>
      <div className="business-reference-preview">
        <p>Informations actuelles · accès vérifié pour votre compte</p>
        <dl>
          {item.fields?.map((f) => (
            <div key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
        {item.href.startsWith("/") && !item.href.startsWith("//") && (
          <Link to={item.href}>Ouvrir {item.label}</Link>
        )}
        <Link to={referenceUrl(reference)}>Voir les éléments liés</Link>
        <CopyReference reference={reference} />
      </div>
    </details>
  );
}
export function useReferenceResolution(references: WorkspaceReference[]) {
  const school = useAuthStore((s) => s.activeSchoolId),
    user = useAuthStore((s) => s.user);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const query = useQuery({
    queryKey: [
      "business-references",
      school,
      user?.id,
      user?.role,
      references.map(({ entityType, entityId }) => ({ entityType, entityId })),
    ],
    queryFn: () => referencesApi.resolve(references),
    enabled: references.length > 0,
    gcTime: 0,
    staleTime: 0,
    refetchInterval: 30000,
    retry: false,
  });

  return {
    items:
      !query.isError && now - query.dataUpdatedAt < 60000
        ? query.data?.items
        : undefined,
    pending: query.isFetching,
    error: query.isError,
    retry: () => { void query.refetch(); },
  };
}
export function ReferenceList({
  references,
}: {
  references: WorkspaceReference[];
}) {
  const q = useReferenceResolution(references);
  if (!references.length) return null;
  return (
    <ul className="business-reference-list">
      {references.map((ref, i) => (
        <li key={ref.entityType + ref.entityId}>
          <ReferenceChip
            reference={ref}
            item={q.items?.[i]}
            pending={q.pending}
            error={q.error}
          />
        </li>
      ))}
    </ul>
  );
}
